import { z } from 'zod'

import {
  Currency,
  EnterpriseBillingConfigCreateTrueUpPeriodDaysEnum,
  type EnterpriseBillingConfigResponse,
  StripeProductOption,
} from '@/api/generated'

// Helper to parse date strings and extract just the date portion (YYYY-MM-DD)
const extractDateString = (isoDate: string | null | undefined): string => {
  if (!isoDate) return ''
  try {
    const date = new Date(isoDate)
    if (isNaN(date.getTime())) return ''
    return date.toISOString().split('T')[0]
  } catch {
    return ''
  }
}

// Helper to convert optional string to nullable number for API payload
const parseOptionalNumber = (val: string | undefined): number | null => {
  if (val === undefined || val === '') return null
  const num = Number(val)
  return Number.isNaN(num) ? null : num
}

const pricingTierFormSchema = z.object({
  maxSeats: z.string(),
  priceCentsPerSeatPerYear: z.string().min(1, 'Price is required'),
})

export type PricingTierFormValues = z.infer<typeof pricingTierFormSchema>

export const billingConfigSchema = z
  .object({
    nextTrueUpDate: z.string().min(1, 'Next true-up date is required'),
    trueUpPeriodDays: z.coerce.number<number>().min(1, 'True-up period is required'),
    stripePriceId: z.string().trim().optional(),
    productName: z.string().min(1, 'Product name is required'),
    paymentTermsDays: z.coerce.number<number>().min(1, 'Payment terms is required'),
    customUnitPriceCents: z.string().optional(),
    currency: z.nativeEnum(Currency, {
      error: (issue) => (issue.input === undefined ? 'Currency is required' : undefined),
    }),
    seatCap: z.string().optional(),
    contractEndDate: z.string(),
    contractPeriodDays: z.string().optional(),
    pricingTiers: z
      .array(pricingTierFormSchema)
      .optional()
      .superRefine((tiers, ctx) => {
        if (!tiers || tiers.length === 0) return
        for (let i = 0; i < tiers.length; i++) {
          // Ensure price is positive for each tier
          const price = Number(tiers[i].priceCentsPerSeatPerYear)
          if (!tiers[i].priceCentsPerSeatPerYear || isNaN(price) || price <= 0 || !Number.isInteger(price)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Price must be a positive whole number',
              path: [i, 'priceCentsPerSeatPerYear'],
            })
          }
          // Ensure maxSeats is positive for all tiers except the last (which is unlimited)
          if (i < tiers.length - 1) {
            const seats = Number(tiers[i].maxSeats)
            if (!tiers[i].maxSeats || isNaN(seats) || seats <= 0 || !Number.isInteger(seats)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Max seats must be a positive whole number',
                path: [i, 'maxSeats'],
              })
            }
            // Ensure strictly increasing max_seats
            if (i > 0 && seats <= Number(tiers[i - 1].maxSeats)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Max seats must be greater than the previous tier',
                path: [i, 'maxSeats'],
              })
            }
          }
        }
      }),
  })
  .superRefine((values, ctx) => {
    // Custom-priced configs (a per-seat override or pricing tiers) must carry an explicit Stripe price ID.
    // Leaving it blank auto-derives the catalog price on the backend, which silently mismatches the
    // enterprise's negotiated Stripe price (the Epic Games incident).
    const isCustomPriced = (values.customUnitPriceCents?.trim() ?? '') !== '' || (values.pricingTiers?.length ?? 0) > 0
    if (isCustomPriced && (values.stripePriceId ?? '') === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'A custom-priced config requires an explicit Stripe price ID — the catalog default cannot be used.',
        path: ['stripePriceId'],
      })
    }
  })

export type BillingConfigFormValues = z.infer<typeof billingConfigSchema>

export const defaultFormValues: BillingConfigFormValues = {
  nextTrueUpDate: '',
  trueUpPeriodDays: 0,
  stripePriceId: '',
  productName: StripeProductOption.FlowBusinessYearly,
  paymentTermsDays: 7,
  customUnitPriceCents: '',
  currency: 'usd',
  seatCap: '',
  contractEndDate: '',
  contractPeriodDays: '',
  pricingTiers: [],
}

/** Convert existing config to form values for edit mode */
export function configToFormValues(config: EnterpriseBillingConfigResponse): BillingConfigFormValues {
  return {
    nextTrueUpDate: extractDateString(config.next_true_up_date),
    trueUpPeriodDays: config.true_up_period_days,
    stripePriceId: config.stripe_price_id ?? '',
    productName: config.product_name,
    paymentTermsDays: config.payment_terms_days ?? 7,
    customUnitPriceCents: config.custom_unit_price_cents?.toString() ?? '',
    currency: config.currency ?? 'usd',
    seatCap: config.seat_cap?.toString() ?? '',
    contractEndDate: extractDateString(config.contract_end_date),
    contractPeriodDays: config.contract_period_days?.toString() ?? '',
    pricingTiers:
      config.pricing_tiers?.map((tier) => ({
        maxSeats: tier.max_seats?.toString() ?? '',
        priceCentsPerSeatPerYear: tier.price_cents_per_seat_per_year.toString(),
      })) ?? [],
  }
}

/** Convert form values to API payload */
export function formValuesToPayload(values: BillingConfigFormValues) {
  const tiers = values.pricingTiers?.length
    ? values.pricingTiers.map((tier, i, arr) => ({
        max_seats: i === arr.length - 1 ? null : Number(tier.maxSeats),
        price_cents_per_seat_per_year: Number(tier.priceCentsPerSeatPerYear),
      }))
    : null

  return {
    next_true_up_date: values.nextTrueUpDate,
    true_up_period_days: values.trueUpPeriodDays as EnterpriseBillingConfigCreateTrueUpPeriodDaysEnum,
    stripe_price_id: values.stripePriceId || null,
    product_name: values.productName,
    payment_terms_days: values.paymentTermsDays,
    custom_unit_price_cents: parseOptionalNumber(values.customUnitPriceCents),
    currency: values.currency,
    seat_cap: parseOptionalNumber(values.seatCap),
    contract_end_date: values.contractEndDate || null,
    contract_period_days: parseOptionalNumber(values.contractPeriodDays),
    pricing_tiers: tiers,
  }
}
